import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useSpring, AnimatePresence } from 'framer-motion';
import { CalendarIcon, ClockIcon, Share2Icon, ArrowRightIcon, ArrowLeftIcon, ChevronUpIcon, TagIcon, BoxIcon, CheckIcon, SparklesIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
const TOC_ITEMS = [{
  id: 'section-1',
  label: 'The problem with spray-and-pray territory coverage'
}, {
  id: 'section-2',
  label: 'What makes a territory high-value?'
}, {
  id: 'section-3',
  label: 'Building a territory scoring model'
}, {
  id: 'section-4',
  label: 'Putting it into practice with Openmart'
}, {
  id: 'section-5',
  label: 'Key takeaways'
}];
const RELATED_POSTS = [{
  id: 1,
  title: 'How to build a high-converting local business lead list in 2026',
  date: 'April 15, 2026',
  category: 'Sales intelligence',
  readTime: '6 min read',
  cover: 'https://cdn.magicpatterns.com/uploads/6NmmoxfkR8JKjQw9Ne5VQB/Concepts.png'
}, {
  id: 2,
  title: 'Introducing Openclaw for sales: Your AI prospecting agent',
  date: 'April 8, 2026',
  category: 'Product updates',
  readTime: '4 min read',
  cover: 'https://cdn.magicpatterns.com/uploads/oFdvqgJyqwqF2M1tB7oZZ5/Input-1.png'
}, {
  id: 3,
  title: 'Why traditional Google Maps scraping is dead (and what to do instead)',
  date: 'March 28, 2026',
  category: 'Local business data',
  readTime: '8 min read',
  cover: 'https://cdn.magicpatterns.com/uploads/afH2mRVBrJjj1RhLCnEgjS/Input-2.png'
}];
export function BlogPost() {
  const [activeSection, setActiveSection] = useState('section-1');
  const [showTocBar, setShowTocBar] = useState(false);
  const [tocExpanded, setTocExpanded] = useState(false);
  const heroRef = useRef<HTMLElement>(null);
  // Reading progress bar
  const {
    scrollYProgress
  } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, {
      rootMargin: '-100px 0px -60% 0px'
    });
    const headings = document.querySelectorAll('h2[id]');
    headings.forEach(h => observer.observe(h));
    return () => headings.forEach(h => observer.unobserve(h));
  }, []);
  useEffect(() => {
    const heroObserver = new IntersectionObserver(([entry]) => {
      // Show TOC bar when hero is out of view
      setShowTocBar(!entry.isIntersecting);
    }, {
      rootMargin: '-100px 0px 0px 0px',
      threshold: 0
    });
    if (heroRef.current) {
      heroObserver.observe(heroRef.current);
    }
    return () => {
      if (heroRef.current) {
        heroObserver.unobserve(heroRef.current);
      }
    };
  }, []);
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-w-full bg-slate-50/50 ecom-aeonik flex flex-col min-h-screen relative" style={{
    fontFamily: 'Aeonik, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }} data-id="element-6447">
      {/* Reading Progress Bar */}
      <motion.div className="fixed top-0 left-0 right-0 h-[3px] bg-blue-600 origin-left z-50" style={{
      scaleX
    }} data-id="element-6448" />

      {/* Sticky TOC Banner - OpenAI style expandable dropdown */}
      <AnimatePresence data-id="element-6449">
        {showTocBar && <motion.div initial={{
        y: -100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -100,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="fixed top-[3px] left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-200/60 shadow-sm" data-id="element-6450">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6451">
              {/* Current section + toggle */}
              <button onClick={() => setTocExpanded(!tocExpanded)} className="w-full flex items-center justify-between py-3.5 text-left" data-id="element-6452">
                <span className="text-sm font-medium text-slate-900 truncate pr-4" data-id="element-6453">
                  {TOC_ITEMS.find(item => item.id === activeSection)?.label || TOC_ITEMS[0].label}
                </span>
                <ChevronUpIcon className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${tocExpanded ? '' : 'rotate-180'}`} data-id="element-6454" />
              </button>

              {/* Expanded dropdown */}
              <AnimatePresence data-id="element-6455">
                {tocExpanded && <motion.nav initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="overflow-hidden border-t border-slate-100" data-id="element-6456">
                    <div className="py-2 flex flex-col" data-id="element-6457">
                      {TOC_ITEMS.map(item => <a key={item.id} href={`#${item.id}`} onClick={e => {
                  scrollToSection(e, item.id);
                  setTocExpanded(false);
                }} className={`block py-2.5 text-sm transition-colors duration-150 ${activeSection === item.id ? 'text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-6458">
                          {item.label}
                        </a>)}
                    </div>
                  </motion.nav>}
              </AnimatePresence>
            </div>
          </motion.div>}
      </AnimatePresence>

      <Navbar data-id="element-6459" />

      <main className="flex-grow pt-28 pb-24 space-y-16" data-id="element-6460">
        {/* HERO SECTION - Inside a white card */}
        <section ref={heroRef} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6461">
          <a href="#/blog" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 mb-6 transition-colors" data-id="element-6462">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-6463" />
            Back to Blog
          </a>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 px-10 md:px-16 lg:px-20 py-14 md:py-20" data-id="element-6464">
            {/* Category + Date — top line */}
            <div className="flex items-center gap-3 text-sm mb-10 md:mb-14" data-id="element-6465">
              <span className="px-3.5 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium" data-id="element-6466">
                Sales intelligence
              </span>
              <span className="text-slate-400" data-id="element-6467">March 12, 2026</span>
            </div>

            {/* Title — generous whitespace */}
            <h1 className="text-3xl md:text-[2.75rem] lg:text-5xl text-slate-900 tracking-tight leading-[1.15] max-w-3xl" data-id="element-6468">
              Stop calling random zips: Territory prioritization for smarter
              local sales
            </h1>

            {/* Thin decorative divider */}
            <div className="w-12 h-[2px] bg-blue-600 rounded-full mt-10 md:mt-14 mb-8 md:mb-10" data-id="element-6469" />

            {/* Metadata Row — airy */}
            <div className="flex flex-wrap items-center gap-5 text-sm text-slate-400" data-id="element-6470">
              <div className="flex items-center gap-2.5" data-id="element-6471">
                <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-[10px] font-medium" data-id="element-6472">
                  OM
                </div>
                <span className="text-slate-600" data-id="element-6473">Openmart Team</span>
              </div>
              <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-6474" />
              <span className="flex items-center gap-1.5" data-id="element-6475">
                <ClockIcon className="w-3.5 h-3.5" data-id="element-6476" />8 min read
              </span>
              <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-6477" />
              <button className="flex items-center gap-1.5 hover:text-blue-600 transition-colors" data-id="element-6478">
                <Share2Icon className="w-3.5 h-3.5" data-id="element-6479" />
                Share
              </button>
            </div>
          </motion.div>
        </section>

        {/* ARTICLE BODY - Inside a white card */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6480">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16" data-id="element-6481">
            {/* Centered Content Area */}
            <article className="max-w-[680px] mx-auto" data-id="element-6482">
              <h2 id="section-1" className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 scroll-mt-32 leading-tight" data-id="element-6483">
                The problem with spray-and-pray territory coverage
              </h2>
              <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85] mb-8" data-id="element-6484">
                Most local sales teams divide territories by zip code or state
                boundary — then wonder why some reps crush quota while others
                struggle. The issue is not effort. It is that not all
                territories are created equal.
              </p>
              <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85] mb-8" data-id="element-6485">
                A zip code in suburban Phoenix with 200 HVAC companies and rapid
                new construction is fundamentally different from a rural zip
                with 12 businesses and no growth signals. Yet many teams treat
                them identically.
              </p>

              <h2 id="section-2" className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 mt-20 scroll-mt-32 leading-tight" data-id="element-6486">
                What makes a territory high-value?
              </h2>
              <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85] mb-8" data-id="element-6487">
                High-value territories share a few characteristics:
              </p>
              <ul className="space-y-4 mb-8" data-id="element-6488">
                {['High density of your target business type (e.g., restaurants, dental practices, HVAC contractors)', 'Growth signals: new business registrations, construction permits, population growth', 'Low competitor saturation: fewer reps from competing vendors already working the area', 'Accessible decision-makers: verified owner contacts with direct emails and phone numbers'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[17px] md:text-lg text-slate-600 leading-[1.85]" data-id="element-6489">
                    <div className="w-2 h-2 rounded-full bg-blue-600 mt-2.5 shrink-0" data-id="element-6490" />
                    <span data-id="element-6491">{item}</span>
                  </li>)}
              </ul>

              <blockquote className="border-l-2 border-blue-600 pl-6 md:pl-8 my-12" data-id="element-6492">
                <p className="text-xl text-slate-700 font-medium leading-[1.85]" data-id="element-6493">
                  "The best sales teams score territories on these dimensions
                  before assigning a single rep."
                </p>
              </blockquote>

              <h2 id="section-3" className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 mt-20 scroll-mt-32 leading-tight" data-id="element-6494">
                Building a territory scoring model
              </h2>
              <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85] mb-8" data-id="element-6495">
                Here is a simple framework you can implement today:
              </p>

              <div className="flex flex-col gap-4 mb-12" data-id="element-6496">
                <div className="bg-white rounded-xl border border-slate-100 p-6" data-id="element-6497">
                  <div className="flex items-start gap-4" data-id="element-6498">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5" data-id="element-6499">
                      1
                    </div>
                    <div data-id="element-6500">
                      <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-6501">
                        Start with business density
                      </h3>
                      <p className="text-slate-600 leading-[1.85]" data-id="element-6502">
                        Use a local business database to count target businesses
                        per zip code or metro area. This is your base layer.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-100 p-6" data-id="element-6503">
                  <div className="flex items-start gap-4" data-id="element-6504">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5" data-id="element-6505">
                      2
                    </div>
                    <div data-id="element-6506">
                      <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-6507">
                        Layer in growth signals
                      </h3>
                      <p className="text-slate-600 leading-[1.85]" data-id="element-6508">
                        Look for areas with increasing business registrations,
                        rising commercial real estate activity, or population
                        growth above the national average.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-100 p-6" data-id="element-6509">
                  <div className="flex items-start gap-4" data-id="element-6510">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5" data-id="element-6511">
                      3
                    </div>
                    <div data-id="element-6512">
                      <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-6513">
                        Assess contact availability
                      </h3>
                      <p className="text-slate-600 leading-[1.85]" data-id="element-6514">
                        A territory with 500 target businesses but no verified
                        owner contacts is less valuable than one with 200
                        businesses and 95% contact coverage.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-100 p-6" data-id="element-6515">
                  <div className="flex items-start gap-4" data-id="element-6516">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5" data-id="element-6517">
                      4
                    </div>
                    <div data-id="element-6518">
                      <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-6519">
                        Factor in competition
                      </h3>
                      <p className="text-slate-600 leading-[1.85]" data-id="element-6520">
                        If three other vendors are already saturating an area,
                        your cost per acquisition will be higher. Prioritize
                        underserved markets.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <h2 id="section-4" className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 mt-20 scroll-mt-32 leading-tight" data-id="element-6521">
                Putting it into practice with Openmart
              </h2>
              <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85] mb-8" data-id="element-6522">
                With access to 200M+ local businesses and 50+ data points per
                record, you can build territory scores programmatically. Filter
                by category, location, employee count, revenue range, and
                technology stack to identify clusters of high-potential
                prospects.
              </p>

              {/* Inline Product Callout */}
              <div className="my-10 bg-slate-50 rounded-2xl border border-slate-100 p-8 flex flex-col sm:flex-row items-center gap-6" data-id="element-6523">
                <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center shrink-0" data-id="element-6524">
                  <SparklesIcon className="w-8 h-8 text-blue-600" data-id="element-6525" />
                </div>
                <div className="flex-1 text-center sm:text-left" data-id="element-6526">
                  <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-6527">
                    Try Openmart for free
                  </h3>
                  <p className="text-slate-600 mb-0" data-id="element-6528">
                    Access 200M+ local businesses with verified owner contacts.
                    No credit card required.
                  </p>
                </div>
                <div className="shrink-0" data-id="element-6529">
                  <Button href="http://app.openmart.com/register" variant="primary" arrow data-id="element-6530">
                    Get started
                  </Button>
                </div>
              </div>

              <p className="text-[17px] md:text-lg text-slate-600 leading-[1.85] mb-8" data-id="element-6531">
                Combine this with our owner finder to ensure you have verified
                decision-maker contacts before your reps pick up the phone. The
                result: higher connect rates, shorter sales cycles, and better
                quota attainment across the board.
              </p>

              <h2 id="section-5" className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 mt-20 scroll-mt-32 leading-tight" data-id="element-6532">
                Key takeaways
              </h2>
              <div className="flex flex-col gap-4 mb-8" data-id="element-6533">
                {['Stop dividing territories by arbitrary geographic boundaries', 'Score territories based on business density, growth signals, contact availability, and competition', 'Use data to continuously re-evaluate and rebalance territories quarterly', 'Invest in verified contact data to maximize rep productivity in high-value areas'].map((item, i) => <div key={i} className="bg-slate-50 rounded-xl p-5 flex items-start gap-4" data-id="element-6534">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-6535">
                      <CheckIcon className="w-4 h-4 text-blue-600" strokeWidth={3} data-id="element-6536" />
                    </div>
                    <span className="text-[17px] md:text-lg text-slate-700 font-medium leading-[1.85]" data-id="element-6537">
                      {item}
                    </span>
                  </div>)}
              </div>
            </article>
          </div>
        </section>

        {/* RELATED POSTS */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6538">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-14 relative overflow-hidden" data-id="element-6539">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-3xl font-bold text-slate-900 mb-10" data-id="element-6540">
              Related <span className="text-blue-600" data-id="element-6541">articles</span>
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-6542">
              {RELATED_POSTS.map((post, index) => <motion.a href="#/blog" key={post.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="group flex flex-col bg-white rounded-2xl border border-slate-200 hover:-translate-y-2 hover:shadow-xl hover:shadow-blue-600/10 hover:border-blue-200 transition-all duration-300 overflow-hidden" data-id="element-6543">
                  <div className="w-full aspect-[16/10] overflow-hidden" data-id="element-6544">
                    <img src={post.cover} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-6545" />
                  </div>
                  <div className="p-6 flex flex-col flex-grow" data-id="element-6546">
                    <div className="mb-3" data-id="element-6547">
                      <span className="inline-block px-2.5 py-1 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700" data-id="element-6548">
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-4 group-hover:text-blue-600 transition-colors leading-snug" data-id="element-6549">
                      {post.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-slate-400 font-medium mt-auto pt-4 border-t border-slate-100" data-id="element-6550">
                      <span data-id="element-6551">{post.date}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-6552" />
                      <span data-id="element-6553">{post.readTime}</span>
                    </div>
                  </div>
                </motion.a>)}
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6554">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-blue-600 rounded-[2.5rem] py-20 md:py-24 px-4 sm:px-6 lg:px-8 text-center shadow-xl relative overflow-hidden" data-id="element-6555">
            {/* Floating ambient blobs */}
            <motion.div animate={{
            x: [0, 60, -50, 0],
            y: [0, -50, 35, 0],
            scale: [1, 1.3, 0.85, 1]
          }} transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-blue-500 blur-[80px] opacity-40 pointer-events-none" data-id="element-6556" />
            <motion.div animate={{
            x: [0, -50, 35, 0],
            y: [0, 35, -40, 0],
            scale: [1, 0.85, 1.25, 1]
          }} transition={{
            duration: 13,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute bottom-[-20%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-400 blur-[60px] opacity-35 pointer-events-none" data-id="element-6557" />

            <h2 className="text-3xl md:text-4xl text-white mb-8 max-w-3xl mx-auto font-bold relative z-10" data-id="element-6558">
              Start reaching local businesses today
            </h2>

            <div className="max-w-md mx-auto relative z-10" data-id="element-6559">
              <div className="mb-8" data-id="element-6560">
                <Button href="http://app.openmart.com/register" variant="secondary" size="lg" fullWidth arrow data-id="element-6561">
                  Try verified contacts for free
                </Button>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm font-medium text-white/90" data-id="element-6562">
                <div className="flex items-center gap-2" data-id="element-6563">
                  <div className="w-4 h-4 rounded-full bg-blue-500/30 flex items-center justify-center" data-id="element-6564">
                    <div className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-6565" />
                  </div>
                  No credit card required
                </div>
                <div className="flex items-center gap-2" data-id="element-6566">
                  <div className="w-4 h-4 rounded-full bg-blue-500/30 flex items-center justify-center" data-id="element-6567">
                    <div className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-6568" />
                  </div>
                  100 free verified contacts
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </main>

      <Footer data-id="element-6569" />
    </div>;
}